import React from "react";
import Link from "next/link";
import '../styles/_agent-installation.module.scss';

type ProductFeature = {
    title: string;
    link: string;
    /** Shown in the "cards" variant; the "split" variant has no icon slot. */
    icon?: React.ReactNode;
};

type Props = {
    productReadyToStart: ProductFeature[];
    /**
     * "cards" — the original row of icon cards with a "Guide" link (OpsAI).
     * "split" — heading on the left, text-only cards with "View Docs ↗" on the
     *           right, on a soft gradient ground (Service Level Objectives).
     */
    variant?: "cards" | "split";
    /** Section heading. Defaults to the original "Pick the path…" copy. */
    title?: React.ReactNode;
    /** Link text on each card. Defaults to "Guide" / "View Docs" per variant. */
    linkLabel?: string;
}

const isExternal = (href: string) => /^https?:\/\//.test(href);

const ProductReadyToStart: React.FC<Props> = ({
    productReadyToStart,
    variant = "cards",
    title,
    linkLabel,
}) => {
    if (variant === "split") {
        return (
            <section className="product-ready-to-start-split">
                {/* Decorative blurred glow, placed exactly as in the design:
                    a tall ellipse laid on its side across the lower right. */}
                <div className="ready-to-start-glow" aria-hidden="true">
                    <div className="ready-to-start-glow-shape">
                        {/* Oversized to leave room for the SVG's 105px blur. */}
                        <div className="ready-to-start-glow-bleed">
                            <img src="/images/platform/slo/ready-to-start-glow.svg" alt="" />
                        </div>
                    </div>
                </div>

                <div className="container">
                    <div className="split-layout">
                        <div className="split-heading">
                            <div className="section-tag">Ready to start?</div>
                            <h2>{title}</h2>
                        </div>

                        <div className="split-cards">
                            {productReadyToStart.map((item, index) => (
                                <div className="ready-to-card" key={index}>
                                    <p className="card-title">{item.title}</p>
                                    <Link
                                        href={item.link}
                                        className="view-docs"
                                        {...(isExternal(item.link)
                                            ? { target: "_blank", rel: "noopener noreferrer" }
                                            : {})}
                                    >
                                        {linkLabel || "View Docs"}
                                        <span className="view-docs-icon" aria-hidden="true">
                                            <img
                                                src="/images/platform/slo/view-docs-arrow.svg"
                                                width={12}
                                                height={12}
                                                alt=""
                                            />
                                        </span>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="product-ready-to-start">
            <div className="container">
                <div className="section-tag">Ready to start?</div>
                <h2>{title ?? <>Pick the path that <br /> matches your environment</>}</h2>

                <div className="d-flex">
                    {productReadyToStart.map((item, index) => (
                        <div className="ready-to-card" key={index}>
                            <div className="info">
                                <div className="icon">{item.icon}</div>
                                <p>{item.title}</p>
                            </div>
                            <Link href={item.link} className="read-more">
                                {linkLabel || "Guide"}
                            </Link>
                        </div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default ProductReadyToStart;
